import { Capacitor } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import * as Sentry from '@sentry/capacitor'

/**
 * Capgo OTA setup — Android only.
 *
 * iOS is excluded because App Store review policy prohibits over-the-air
 * code updates that change app functionality (and Apple would reject
 * binaries that ship a third-party hot-update mechanism).
 *
 * On Android: Capgo's plugin checks for new bundle versions on app start,
 * downloads silently in the background, applies on next cold launch.
 *
 * Bundles are pushed via `npx @capgo/cli upload` from a dev machine (see
 * mobile/README.md release section).
 */
export async function setupOta(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  if (Capacitor.getPlatform() !== 'android') return

  try {
    await CapacitorUpdater.addListener('updateAvailable', (info) => {
      Sentry.addBreadcrumb({
        category: 'ota',
        level: 'info',
        message: 'OTA update available',
        data: info,
      })
    })

    await CapacitorUpdater.addListener('updateFailed', (info) => {
      Sentry.captureMessage('OTA update failed', {
        level: 'warning',
        extra: info as Record<string, unknown>,
      })
    })

    // Tell the plugin the current bundle booted successfully so it doesn't
    // roll back on next launch (Capgo treats apps that crash before
    // notifyAppReady() as a failed update and reverts).
    await CapacitorUpdater.notifyAppReady()
  } catch (err) {
    // Plugin not registered yet, or device offline — fail open.
    console.warn('[ota] setup failed', err)
  }
}
